import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, BookOpen, Feather, Clock, X } from "lucide-react";
import FIELD_NOTES from "../content/fieldNotes/generated";
import { IMAGES } from "../mock";
import { getLang } from "../i18n";
import { Reveal, PageHero, Ornament } from "../components/bits";
import VideoBackground from "../components/VideoBackground";
import MonographModal from "../components/MonographModal";
import FieldNotesSubscribeModal from "../components/FieldNotesSubscribeModal";
import { Input } from "../components/ui/input";

const OG_IMAGE = IMAGES.cosmicLight;

const T = {
  en: {
    docTitle: "Field Notes & Publications — Nonlocal Field Harmonization",
    docDesc:
      "Reflections on nondual spaceholding, subtle mechanics, and the science of unconditioned presence — plus the free Master Monograph by Dr. Ing. Luca Pizzimbone.",
    heroTitle: "Field Notes & Publications",
    heroSub: "Reflections on nondual spaceholding, subtle mechanics, and the science of unconditioned presence.",
    featured: "Featured · Free",
    monoTitle: "Nonlocal Field Harmonization — A Master Monograph",
    monoSub: "Download the complete foundational text, freely.",
    monoDesc:
      "The Architecture of Awareness, Science, Lineage, and Contextual Foundations, by Dr. Ing. Luca Pizzimbone.",
    download: "Download the Monograph",
    subscribe: "Subscribe to Field Notes",
    monoHint:
      "The download includes an optional Field Notes signup. Already have the Monograph? Subscribe to Field Notes only.",
    searchPlaceholder: "Search the Field Notes…",
    clear: "Clear",
    empty: "No Field Notes match your search.",
    issue: "FIELD NOTE",
    closing: "If this resonates, you are warmly welcome to the free Sunday Common Session.",
    closingCta: "Enter the Sunday Field",
    locale: "en-US",
  },
  de: {
    docTitle: "Field Notes & Publikationen — Nichtlokale Feldharmonisierung",
    docDesc:
      "Reflexionen über nonduales Spaceholding, subtile Mechanik und die Wissenschaft der bedingungslosen Präsenz — sowie die kostenlose Master-Monographie von Dr. Ing. Luca Pizzimbone.",
    heroTitle: "Field Notes & Publikationen",
    heroSub: "Reflexionen über nonduales Spaceholding, subtile Mechanik und die Wissenschaft der bedingungslosen Präsenz.",
    featured: "Empfohlen · Kostenlos",
    monoTitle: "Nichtlokale Feldharmonisierung — Eine Master-Monographie",
    monoSub: "Lade den vollständigen Grundlagentext kostenlos herunter.",
    monoDesc:
      "Die Architektur des Gewahrseins, Wissenschaft, Linie und kontextuelle Grundlagen, von Dr. Ing. Luca Pizzimbone.",
    download: "Monographie herunterladen",
    subscribe: "Field Notes abonnieren",
    monoHint:
      "Der Download enthält eine optionale Field-Notes-Anmeldung. Du hast die Monographie bereits? Abonniere nur die Field Notes.",
    searchPlaceholder: "Field Notes durchsuchen…",
    clear: "Zurücksetzen",
    empty: "Keine Field Notes passen zu deiner Suche.",
    issue: "FIELD NOTE",
    closing: "Wenn dich das berührt, bist du herzlich zur kostenlosen Sonntagssitzung eingeladen.",
    closingCta: "Tritt in das Sonntagsfeld ein",
    locale: "de-DE",
  },
};

const FieldNotes = () => {
  const location = useLocation();
  const lang = getLang(location.pathname);
  const t = T[lang];
  const basePath = lang === "de" ? "/de/field-notes" : "/field-notes";
  const contactsPath = lang === "de" ? "/de/kontakt" : "/contacts";

  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(null);

  const notes = useMemo(() => FIELD_NOTES.filter((n) => n.lang === lang), [lang]);

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString(t.locale, { year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
    document.title = t.docTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t.docDesc);
  }, [t]);

  // Reset filters when switching language.
  useEffect(() => {
    setQuery("");
    setActiveCat(null);
  }, [lang]);

  const categories = useMemo(() => {
    const set = [];
    notes.forEach((n) => {
      if (n.category && !set.includes(n.category)) set.push(n.category);
    });
    return set;
  }, [notes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notes.filter((n) => {
      if (activeCat && n.category !== activeCat) return false;
      if (!q) return true;
      const haystack = [n.title, n.subtitle, n.excerpt, (n.tags || []).join(" "), n.body]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [notes, query, activeCat]);

  const issueLabel = (n) => `${t.issue} #${String(n.issue).padStart(2, "0")}`;

  return (
    <main className="video-page" data-testid="field-notes-page">
      <PageHero title={t.heroTitle} sub={t.heroSub} align="center" light />
      <VideoBackground veil={0.5} />

      {/* MASTER MONOGRAPH — featured */}
      <section className="section-tight">
        <div className="container-page">
          <Reveal>
            <div className="fn-monograph soft-card" data-testid="monograph-feature-card">
              <div className="fn-monograph-mark" aria-hidden="true">
                <BookOpen size={26} />
              </div>
              <div className="fn-monograph-copy">
                <span className="eyebrow" style={{ color: "var(--gold-strong)" }}>
                  {t.featured}
                </span>
                <h2 className="h-section" style={{ marginTop: "0.5rem" }}>
                  {t.monoTitle}
                </h2>
                <p className="h-sub" style={{ marginTop: "0.5rem" }}>
                  {t.monoSub}
                </p>
                <p className="lead" style={{ marginTop: "0.9rem" }}>
                  {t.monoDesc}
                </p>
                <div className="fn-monograph-actions">
                  <MonographModal>
                    <button type="button" className="btn-gold" data-testid="download-monograph-button">
                      <BookOpen size={16} /> {t.download}
                    </button>
                  </MonographModal>
                  <FieldNotesSubscribeModal>
                    <button type="button" className="btn-ghost" data-testid="subscribe-field-notes-button">
                      <Feather size={15} /> {t.subscribe}
                    </button>
                  </FieldNotesSubscribeModal>
                </div>
                <p className="fn-monograph-hint">{t.monoHint}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SEARCH + FILTERS */}
      <section style={{ paddingTop: 0 }}>
        <div className="container-page">
          <Reveal className="fn-controls">
            <div className="fn-search">
              <Search size={18} className="fn-search-icon" />
              <Input
                type="text"
                data-testid="field-notes-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="fn-search-input"
              />
              {query && (
                <button
                  type="button"
                  className="fn-search-clear"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  data-testid="field-notes-search-clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <div className="fn-pills">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`fn-pill ${activeCat === c ? "active" : ""}`}
                  onClick={() => setActiveCat(activeCat === c ? null : c)}
                  data-testid={`field-notes-pill-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
              {activeCat && (
                <button
                  type="button"
                  className="fn-pill clear"
                  onClick={() => setActiveCat(null)}
                  data-testid="field-notes-pill-clear"
                >
                  {t.clear} <X size={13} />
                </button>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* GRID */}
      <section className="section-tight" style={{ paddingTop: "1.5rem" }}>
        <div className="container-page">
          {filtered.length === 0 ? (
            <p className="lead" style={{ textAlign: "center" }} data-testid="field-notes-empty">
              {t.empty}
            </p>
          ) : (
            <div className="fn-grid" data-testid="field-notes-grid">
              {filtered.map((n, i) => (
                <Reveal
                  key={n.slug}
                  delay={(i % 3) * 90}
                  as={Link}
                  className="fn-card soft-card"
                  to={`${basePath}/${n.slug}`}
                  data-testid={`field-note-card-${n.slug}`}
                >
                  <div className="fn-card-top">
                    <span className="fn-issue">{issueLabel(n)}</span>
                    <span className="fn-cat">{n.category}</span>
                  </div>
                  <h3 className="fn-card-title">{n.title}</h3>
                  <p className="fn-card-sub">{n.subtitle}</p>
                  <p className="fn-card-excerpt">{n.excerpt}</p>
                  <div className="fn-card-meta">
                    <span>{fmtDate(n.date)}</span>
                    <span className="fn-dot">·</span>
                    <span className="fn-reading"><Clock size={13} /> {n.readingTime}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section-tight bloom" style={{ paddingTop: "2rem" }}>
        <div className="container-page">
          <Reveal className="container-narrow" style={{ textAlign: "center" }}>
            <Ornament style={{ marginBottom: "1rem", justifyContent: "center", display: "inline-flex" }} />
            <p className="lead">{t.closing}</p>
            <Link to={contactsPath} className="btn-gold" style={{ marginTop: "1.5rem" }}>
              {t.closingCta}
            </Link>
          </Reveal>
        </div>
      </section>

      <meta property="og:image" content={OG_IMAGE} />
    </main>
  );
};

export default FieldNotes;
